import { rowLockIndicator } from "../../../src/taskpane/config/constants";

/**
 * Which selected rows a command may actually act on.
 *
 * The naive answer — Fluent's `getSelection()` result — is wrong in three ways once the
 * grid can change on its own (realtime ItemHub broadcasts):
 *
 * 1. **Stale objects.** `getSelection()` memoizes its array and only invalidates it on a
 *    selection CHANGE. Replacing a row with a re-read copy keeps the same key at the same
 *    index, so no change fires and the cached array still holds the PRE-update objects.
 *    Gating on those reads stale `CheckoutUser`/`_ModerationStatus`/permissions.
 * 2. **Vanished rows.** A row deleted underneath the user must stop being commandable at
 *    once, not after the next selection event.
 * 3. **Locked rows.** A row automation locks mid-selection cannot be DESELECTED: Fluent's
 *    `setIndexSelected` no-ops on any index its `canSelectItem` now rejects (and
 *    `canSelectItem` rejects locked rows, which is what stops NEW selections). Filtering
 *    here is therefore the only reliable exclusion.
 *
 * Resolving the selection back through the live `rows` array fixes all three at once.
 */

/** Stable identity for "nothing to command", so memo consumers do not re-key needlessly. */
export const NO_COMMANDABLE_ITEMS: any[] = [];

export const resolveCommandableItems = (
    selectedItems: any[],
    rows: any[],
    getRowKey: (item: any) => string
): any[] => {
    if (!(selectedItems?.length > 0)) {
        return NO_COMMANDABLE_ITEMS;
    }
    const selectedKeys = new Set((selectedItems ?? []).filter(Boolean).map(getRowKey));
    return (rows ?? []).filter(row => row && selectedKeys.has(getRowKey(row)) && row[rowLockIndicator] !== true);
};
